import { memo, useCallback } from 'react'
import Box from './Box'
import GrooverText from './GrooverText'
import styled from '@emotion/styled'
import { ReturningUserProfile, StorageKey } from '../hooks/useLocalStorageItemHelper'

type Props = {
  returningProfile: ReturningUserProfile
  onApply: () => void
  onDismiss: () => void
}

const BannerBox = styled(Box)`
  background-color: #eef4ff;
  border: 1px solid #bdd7ff;
  border-radius: 12px;
  padding: 14px 16px;
  position: relative;
`

const TitleText = styled.span`
  font-weight: 600;
  font-size: 15px;
  color: rgba(39, 42, 48, 1);
  padding-right: 28px;
`

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(39, 42, 48, 0.5);
  font-size: 14px;
  line-height: 1;
  flex-shrink: 0;
  padding: 0;
  border-radius: 50%;

  &:hover {
    background-color: rgba(39, 42, 48, 0.08);
    color: rgba(39, 42, 48, 1);
  }
`

const UseDetailsButton = styled.button`
  flex: 1;
  border: none;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 16px;
  background-color: rgba(59, 89, 236, 1);
  color: #ffffff;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.4;
  white-space: nowrap;

  &:hover {
    background-color: rgba(40, 65, 190, 1);
  }
`

const ClearLink = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0 4px;
  color: rgba(39, 42, 48, 0.5);
  font-size: 13px;
  line-height: 1.4;
  white-space: nowrap;
  text-decoration: underline;
  flex-shrink: 0;

  &:hover {
    color: rgba(39, 42, 48, 0.8);
  }
`

const ReturningUserBanner = ({ returningProfile, onApply, onDismiss }: Props) => {
  const handleApply = useCallback(() => {
    onApply()
    onDismiss()
  }, [onApply, onDismiss])

  const handleClear = useCallback(() => {
    try {
      localStorage.removeItem(StorageKey.RETURNING_USER_PROFILE)
    } catch {
      // ignore storage errors
    }
    onDismiss()
  }, [onDismiss])

  return (
    <BannerBox fullWidth gap="6px">
      <CloseButton type="button" onClick={onDismiss} aria-label="Close">
        ✕
      </CloseButton>
      <TitleText>
        <GrooverText textKey="registerReturningUserBannerTitle" />
      </TitleText>
      <span style={{ fontSize: '13px', color: 'rgba(39, 42, 48, 0.65)' }}>
        <GrooverText textKey="registerReturningUserBannerDescription" />
      </span>
      <span style={{ fontSize: '12px', color: 'rgba(39, 42, 48, 0.5)', fontStyle: 'italic' }}>
        {returningProfile.name} · {returningProfile.email} · {returningProfile.phone}
      </span>
      <Box direction="row" gap="8px" align="center" style={{ flexWrap: 'nowrap' }}>
        <UseDetailsButton type="button" onClick={handleApply}>
          <GrooverText textKey="registerReturningUserBannerUseButton" />
        </UseDetailsButton>
        <ClearLink type="button" onClick={handleClear}>
          <GrooverText textKey="registerReturningUserBannerClearInfo" style={{ color: 'inherit' }} />
        </ClearLink>
      </Box>
    </BannerBox>
  )
}

export default memo(ReturningUserBanner)
